import { MarcaBlancaAuthResponse, Branch } from "../../hooks/useMarcaBlancaAuth";

export const SET_MARCA_BLANCA_VALIDATED = "SET_MARCA_BLANCA_VALIDATED";
export const SET_MARCA_BLANCA_CLIENT = "SET_MARCA_BLANCA_CLIENT";
export const BLOCK_MARCA_BLANCA = "BLOCK_MARCA_BLANCA";
export const SHOW_MARCA_BLANCA_CART_LIMIT = "SHOW_MARCA_BLANCA_CART_LIMIT";
export const HIDE_MARCA_BLANCA_CART_LIMIT = "HIDE_MARCA_BLANCA_CART_LIMIT";
export const SET_MARCA_BLANCA_HASH_VALIDATING =
  "SET_MARCA_BLANCA_HASH_VALIDATING";
export const SET_MARCA_BLANCA_HASH_FAILED = "SET_MARCA_BLANCA_HASH_FAILED";
export const RESET_MARCA_BLANCA_HASH = "RESET_MARCA_BLANCA_HASH";
export const SET_MARCA_BLANCA_SELLER_CODE = "SET_MARCA_BLANCA_SELLER_CODE";
export const CLEAR_MARCA_BLANCA_SELLER_CODE =
  "CLEAR_MARCA_BLANCA_SELLER_CODE";
export const SET_MARCA_BLANCA_BRANCH = "SET_MARCA_BLANCA_BRANCH";

export type MarcaBlancaHashStatus = "idle" | "validating" | "failed";

// Datos del comercio que prellenan el checkout de Marca Blanca.
export type MarcaBlancaClient = {
  firstName: string;
  lastName: string;
  documentTypeId: string;
  email: string;
  addressDetail: string;
  department: string;
  city: string;
  codeDane: string;
  mobile: string;
  branches: Branch[];
  maxDataphoneUnits: number;
};

// Guarda las credenciales validadas en el modal para poder re-verificar en los
// chokepoints (abrir producto / checkout) contra el backend.
export const setMarcaBlancaValidated = (payload: {
  clientId: string;
  documentNumber: string;
  documentTypeId: string | number;
  documentTypeCode?: string;
}) => ({
  type: SET_MARCA_BLANCA_VALIDATED,
  payload,
});

// Se normaliza a string porque el formulario de Marca Blanca (inputs y selects)
// trabaja con strings.
const toMarcaBlancaClient = (
  response: MarcaBlancaAuthResponse
): MarcaBlancaClient => {
  const data: any = response?.data ?? {};
  const branches = Array.isArray(data.branches) ? data.branches : [];
  return {
    firstName: String(data.firstName ?? ""),
    lastName: String(data.lastName ?? ""),
    documentTypeId: String(data.documentTypeId ?? ""),
    email: String(data.email ?? ""),
    addressDetail: String(data.addressDetail ?? ""),
    department: String(data.address?.department?.value ?? ""),
    city: String(data.address?.city?.value ?? ""),
    codeDane: String(data.address?.city?.codeDane ?? ""),
    mobile: String(data.mobile ?? ""),
    branches,
    maxDataphoneUnits: branches.reduce(
      (max: number, branch: any) => Math.max(max, Number(branch?.dataphonesAvailable) || 0),
      0
    ),
  };
};

// Guarda los datos del comercio para prellenar el checkout de Marca Blanca.
export const setMarcaBlancaClient = (response: MarcaBlancaAuthResponse) => ({
  type: SET_MARCA_BLANCA_CLIENT,
  payload: toMarcaBlancaClient(response),
});

// Marca al cliente como NO validado y limpia las credenciales. `lastBlockedAt`
// cambia en cada bloqueo para forzar el re-render que reabre el modal.
export const blockMarcaBlanca = () => ({
  type: BLOCK_MARCA_BLANCA,
});

// Muestra/oculta el modal de "un (1) datáfono por carrito" en la tienda MB.
export const showMarcaBlancaCartLimit = () => ({
  type: SHOW_MARCA_BLANCA_CART_LIMIT,
});

export const hideMarcaBlancaCartLimit = () => ({
  type: HIDE_MARCA_BLANCA_CART_LIMIT,
});

export const setMarcaBlancaHashValidating = () => ({
  type: SET_MARCA_BLANCA_HASH_VALIDATING,
});

export const setMarcaBlancaHashFailed = (
  response: MarcaBlancaAuthResponse | null
) => ({
  type: SET_MARCA_BLANCA_HASH_FAILED,
  payload: response,
});

export const resetMarcaBlancaHash = () => ({
  type: RESET_MARCA_BLANCA_HASH,
});

// Guarda el código de vendedor ya validado con éxito en el paso 2, para que
// la confirmación sobreviva la navegación al paso 3.
export const setMarcaBlancaSellerCode = (sellerCode: string) => ({
  type: SET_MARCA_BLANCA_SELLER_CODE,
  payload: sellerCode,
});

// Limpia el código guardado (el usuario editó el campo o la validación falló).
export const clearMarcaBlancaSellerCode = () => ({
  type: CLEAR_MARCA_BLANCA_SELLER_CODE,
});

// Guarda la sucursal elegida en el paso 2. El paso 3 muestra su dirección
export const setMarcaBlancaBranch = (branch: Branch | null) => ({
  type: SET_MARCA_BLANCA_BRANCH,
  payload: branch,
});
